import type { StateCreator } from 'zustand';
import type { DashboardClinicOption } from '../../services/api';
import { getDashboardClinics } from '../../services/dashboardClinicsApi';
export type DashboardClinicsStatus = 'idle' | 'loading' | 'success' | 'error';
export type DashboardClinicIdentity = {
  userId: string;
  agencyId: string;
} | null;

export interface DashboardClinicSlice {
  dashboardClinics: DashboardClinicOption[];
  dashboardClinicsStatus: DashboardClinicsStatus;
  dashboardClinicsError: string | null;
  dashboardClinicsIdentity: DashboardClinicIdentity;
  fetchDashboardClinics: (identity: {
    userId: string;
    agencyId: string;
  }) => Promise<void>;
  clearDashboardClinics: () => void;
}

const identityKey = (identity: DashboardClinicIdentity) =>
  identity ? `${identity.userId}:${identity.agencyId}` : null;

export const dashboardClinicSlice: StateCreator<DashboardClinicSlice> = (
  set,
  get
) => {
  let generation = 0;
  let controller: AbortController | null = null;

  return {
    dashboardClinics: [],
    dashboardClinicsStatus: 'idle',
    dashboardClinicsError: null,
    dashboardClinicsIdentity: null,

    fetchDashboardClinics: async identity => {
      const requestedKey = identityKey(identity);
      const currentKey = identityKey(get().dashboardClinicsIdentity);
      if (
        requestedKey === currentKey &&
        get().dashboardClinicsStatus === 'loading'
      ) {
        return;
      }

      generation += 1;
      const requestGeneration = generation;
      controller?.abort();
      controller = new AbortController();
      set({
        dashboardClinics: [],
        dashboardClinicsStatus: 'loading',
        dashboardClinicsError: null,
        dashboardClinicsIdentity: identity,
      });

      try {
        const clinics = await getDashboardClinics({
          signal: controller.signal,
        });
        const current = get();
        if (
          requestGeneration !== generation ||
          identityKey(current.dashboardClinicsIdentity) !== requestedKey
        ) {
          return;
        }
        set({
          dashboardClinics: clinics,
          dashboardClinicsStatus: 'success',
          dashboardClinicsError: null,
        });
      } catch (error) {
        const current = get();
        if (
          requestGeneration !== generation ||
          identityKey(current.dashboardClinicsIdentity) !== requestedKey
        ) {
          return;
        }
        if (
          error instanceof Error &&
          (error.name === 'AbortError' || error.name === 'CanceledError')
        ) {
          return;
        }
        console.error('Error fetching dashboard clinics:', error);
        set({
          dashboardClinics: [],
          dashboardClinicsStatus: 'error',
          dashboardClinicsError: 'Unable to load clinics. Please try again.',
        });
      }
    },

    clearDashboardClinics: () => {
      generation += 1;
      controller?.abort();
      controller = null;
      set({
        dashboardClinics: [],
        dashboardClinicsStatus: 'idle',
        dashboardClinicsError: null,
        dashboardClinicsIdentity: null,
      });
    },
  };
};
